import { CommonModule } from '@angular/common';
import { Component, Directive, ElementRef, Inject, Input, NgModule, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

type TimePeriod = 'AM' | 'PM';

interface TimepickerDialogData {
  time: string;
}

@Component({
  standalone: false,
  selector: 'ngx-material-timepicker-clock-dialog',
  template: `
    <div class="compat-timepicker">
      <header class="compat-timepicker__header">
        <button type="button" class="compat-timepicker__time" (click)="mode = 'hour'">
          {{ hour }}:<span>{{ minuteLabel }}</span>
        </button>
        <div class="compat-timepicker__periods">
          <button type="button" [class.is-active]="period === 'AM'" (click)="period = 'AM'">AM</button>
          <button type="button" [class.is-active]="period === 'PM'" (click)="period = 'PM'">PM</button>
        </div>
      </header>

      <main class="compat-timepicker__face">
        <div class="compat-timepicker__hand" [style.transform]="handTransform"></div>
        <button
          *ngFor="let option of visibleOptions"
          type="button"
          class="compat-timepicker__option"
          [class.is-active]="option.value === selectedValue"
          [style.left.%]="option.left"
          [style.top.%]="option.top"
          (click)="select(option.value)"
        >
          {{ option.label }}
        </button>
      </main>

      <footer class="compat-timepicker__actions">
        <button mat-button type="button" color="primary" (click)="cancel()">CANCEL</button>
        <button mat-button type="button" color="primary" (click)="confirm()">OK</button>
      </footer>
    </div>
  `,
  styles: [
    `
      .compat-timepicker {
        background: #fff;
        width: 300px;
      }

      .compat-timepicker__header {
        align-items: center;
        background: #2196f3;
        color: #fff;
        display: flex;
        height: 90px;
        justify-content: center;
      }

      .compat-timepicker__time {
        background: transparent;
        border: 0;
        color: inherit;
        cursor: pointer;
        font-size: 52px;
        line-height: 1;
        padding: 0;
      }

      .compat-timepicker__time span {
        opacity: 0.65;
      }

      .compat-timepicker__periods {
        display: flex;
        flex-direction: column;
        margin-left: 12px;
      }

      .compat-timepicker__periods button {
        background: transparent;
        border: 0;
        color: rgba(255, 255, 255, 0.65);
        cursor: pointer;
        font-size: 18px;
        line-height: 24px;
        padding: 0;
        text-align: left;
      }

      .compat-timepicker__periods button.is-active {
        color: #fff;
      }

      .compat-timepicker__face {
        background: #eee;
        border-radius: 50%;
        height: 270px;
        margin: 16px auto;
        position: relative;
        width: 270px;
      }

      .compat-timepicker__hand {
        background: #2196f3;
        height: 2px;
        left: 50%;
        position: absolute;
        top: 50%;
        transform-origin: left center;
        width: 103px;
      }

      .compat-timepicker__option {
        align-items: center;
        background: transparent;
        border: 0;
        border-radius: 50%;
        color: #555;
        cursor: pointer;
        display: flex;
        font-size: 16px;
        height: 34px;
        justify-content: center;
        margin-left: -17px;
        margin-top: -17px;
        padding: 0;
        position: absolute;
        width: 34px;
      }

      .compat-timepicker__option.is-active {
        background: #2196f3;
        color: #fff;
      }

      .compat-timepicker__actions {
        display: flex;
        justify-content: flex-end;
        padding: 8px 16px 16px;
      }
    `
  ]
})
export class NgxMaterialTimepickerClockDialogComponent {
  hour = 12;
  minute = 0;
  period: TimePeriod = 'AM';
  mode: 'hour' | 'minute' = 'hour';

  readonly hourOptions = this.createOptions(
    Array.from({ length: 12 }, (_, index) => {
      const value = index + 1;
      return { value, label: value.toString() };
    }),
    -60
  );

  readonly minuteOptions = this.createOptions(
    Array.from({ length: 12 }, (_, index) => {
      const value = index * 5;
      return { value, label: value.toString().padStart(2, '0') };
    }),
    -90
  );

  constructor(
    private dialogRef: MatDialogRef<NgxMaterialTimepickerClockDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: TimepickerDialogData
  ) {
    this.applyTime(data?.time);
  }

  get minuteLabel() {
    return this.minute.toString().padStart(2, '0');
  }

  get selectedValue() {
    return this.mode === 'hour' ? this.hour : this.minute;
  }

  get visibleOptions() {
    return this.mode === 'hour' ? this.hourOptions : this.minuteOptions;
  }

  get handTransform() {
    const value = this.mode === 'hour' ? this.hour % 12 : this.minute / 5;
    return `rotate(${value * 30 - 90}deg)`;
  }

  select(value: number) {
    if (this.mode === 'hour') {
      this.hour = value;
      this.mode = 'minute';
    } else {
      this.minute = value;
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  confirm() {
    this.dialogRef.close(`${this.hour}:${this.minuteLabel} ${this.period}`);
  }

  private applyTime(time: string) {
    const match = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(time ?? '');
    if (!match) return;

    let parsedHour = Number(match[1]);
    this.minute = Number(match[2]);
    this.period = (match[3]?.toUpperCase() as TimePeriod) || (parsedHour >= 12 ? 'PM' : 'AM');

    if (parsedHour === 0) {
      parsedHour = 12;
    } else if (parsedHour > 12) {
      parsedHour -= 12;
    }

    this.hour = parsedHour;
  }

  private createOptions(options: Array<{ value: number; label: string }>, startAngle: number) {
    return options.map((option, index) => {
      const angle = index * 30 + startAngle;
      const radians = (angle * Math.PI) / 180;
      return {
        ...option,
        left: 50 + Math.cos(radians) * 38,
        top: 50 + Math.sin(radians) * 38
      };
    });
  }
}

@Component({
  standalone: false,
  selector: 'ngx-material-timepicker',
  template: ''
})
export class NgxMaterialTimepickerComponent {
  @Input() timepickerClass = '';
  @Input() defaultTime = '';

  private input?: NgxMaterialTimepickerDirective;

  constructor(private dialog: MatDialog) {}

  registerInput(input: NgxMaterialTimepickerDirective): void {
    this.input = input;
  }

  open(): void {
    const dialogRef = this.dialog.open(NgxMaterialTimepickerClockDialogComponent, {
      autoFocus: false,
      panelClass: this.timepickerClass,
      data: { time: this.input?.value || this.defaultTime }
    });

    dialogRef.afterClosed().subscribe((time: string | undefined) => {
      if (time) {
        this.input?.setValue(time);
      }
    });
  }
}

@Component({
  standalone: false,
  selector: 'ngx-material-timepicker-toggle',
  template: `
    <button class="ngx-material-timepicker-toggle" type="button" [disabled]="disabled" (click)="open($event)">
      <ng-content></ng-content>
    </button>
  `
})
export class NgxMaterialTimepickerToggleComponent {
  @Input('for') timepicker?: NgxMaterialTimepickerComponent;
  @Input() disabled = false;

  open(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.timepicker?.open();
  }
}

@Directive({
  standalone: false,
  selector: '[ngxMaterialTimepickerToggleIcon]'
})
export class NgxMaterialTimepickerToggleIconDirective {}

@Directive({
  standalone: false,
  selector: 'input[ngxTimepicker]',
  host: {
    '(click)': 'onClick($event)',
    '(keydown.enter)': 'onClick($event)',
    '(keydown.space)': 'onClick($event)'
  }
})
export class NgxMaterialTimepickerDirective {
  private timepicker?: NgxMaterialTimepickerComponent;

  @Input()
  set ngxTimepicker(timepicker: NgxMaterialTimepickerComponent | undefined) {
    this.timepicker = timepicker;
    this.timepicker?.registerInput(this);
  }

  @Input() disableClick = false;

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  get value() {
    return this.ngControl?.control?.value || this.elementRef.nativeElement.value;
  }

  onClick(event: Event) {
    if (this.disableClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.timepicker?.open();
  }

  setValue(time: string) {
    if (this.ngControl?.control) {
      this.ngControl.control.setValue(time);
      this.ngControl.control.markAsDirty();
      this.ngControl.control.markAsTouched();
    } else {
      this.elementRef.nativeElement.value = time;
      this.elementRef.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
      this.elementRef.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

@NgModule({
  declarations: [
    NgxMaterialTimepickerClockDialogComponent,
    NgxMaterialTimepickerComponent,
    NgxMaterialTimepickerToggleComponent,
    NgxMaterialTimepickerToggleIconDirective,
    NgxMaterialTimepickerDirective
  ],
  exports: [
    NgxMaterialTimepickerComponent,
    NgxMaterialTimepickerToggleComponent,
    NgxMaterialTimepickerToggleIconDirective,
    NgxMaterialTimepickerDirective
  ],
  imports: [CommonModule, MatButtonModule, MatDialogModule]
})
export class NgxMaterialTimepickerModule {}
